import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TrialBalanceService } from 'src/app/core/services/trial-balance.service';
import { CommonService } from 'src/app/core/services/common.service';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';
import { NgxSpinnerService } from 'ngx-spinner';
import { VoucherService } from 'src/app/core/services/voucher.service';

@Component({
  selector: 'app-ledger-details',
  templateUrl: './ledger-details.component.html',
  styleUrls: ['./ledger-details.component.scss']
})
export class LedgerDetailsComponent implements OnInit {
  ledgerId: number = 0;
  storeCode: string = '';
  storeName: string = '';
  reportBasis: string = 'Accrual';
  ledgerName: string = '';

  minDate: string = '';
  maxDate: string = '';
  ledgerDetails: any[] = [];
  voucherMap: { [key: string]: number } = {};

  constructor(
    private route: ActivatedRoute,
    private tbService: TrialBalanceService,
    private commonService: CommonService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private voucherService: VoucherService
  ) { }

  ngOnInit(): void {
    this.ledgerId = Number(this.route.snapshot.paramMap.get('ledgerId'));
    const rawData = sessionStorage.getItem(environment.dataKey);
    const userData = rawData ? JSON.parse(this.commonService.atou(rawData)) : [];
    this.storeCode = userData[0]?.storecode;
    this.storeName = userData[0]?.storename || '';

    const yearData = this.commonService.getYearData();
    this.minDate = yearData?.FinYearFrom?.substring(0, 10) || '';
    this.maxDate = yearData?.FinYearTill?.substring(0, 10) || '';
    this.voucherService.getVouchers(this.storeCode).subscribe({
      next: (vouchers: any[]) => {
        vouchers.forEach(v => {
          if (v.VoucherNo && v.VoucherId) {
            this.voucherMap[v.VoucherNo] = v.VoucherId;
          }
        });
        this.fetchLedgerDetails();
      },
      error: (err) => {
        console.error("Failed to fetch voucher list:", err);
        this.fetchLedgerDetails();
      }
    });
  }

  fetchLedgerDetails() {
    this.spinner.show();
    this.tbService.getLedgerDetails(this.storeCode, this.minDate, this.maxDate, this.ledgerId).subscribe({
      next: (data: any[]) => {
        this.ledgerDetails = data;
        this.ledgerName = data.length ? data[0].LedgerName : '';
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  goBack() {
    this.router.navigate(['/epage/fa/trial-balance']);
  }

  openVoucher(entry: any) {
    const voucherId = entry.VoucherId || this.voucherMap[entry.VoucherNo];
    if (!voucherId) {
      console.error("VoucherId not found for entry:", entry);
      return;
    }

    let backUrl = this.router.url.replace('/epage', '/fa');
    this.router.navigate(['/epage/fa/voucher', voucherId], {
      queryParams: { mode: 'view', backUrl: this.router.url }
    });

  }

  exportExcel() {
    const exportData = this.ledgerDetails.map(entry => ({
      'Voucher No': entry.VoucherNo,
      'Voucher Date': new Date(entry.VoucherDate).toLocaleDateString('en-GB'),
      'Narration': entry.Narration,
      'Debit': entry.Debit || 0,
      'Credit': entry.Credit || 0,
      'Balance': entry.Balance || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'LedgerDetails');
    XLSX.writeFile(workbook, `LedgerDetails_${this.ledgerName}.xlsx`);
  }

  printLedger() {
    const formatDate = (date: any) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-GB');
    };

    let printContents = `
  <style>
    table { 
      width: 100%; 
      border-collapse: collapse; 
    }
    th, td { 
      padding: 6px; 
      text-align: right; 
      border: 1px solid #000;   /* ✅ vertical + horizontal lines */
    }
    th:first-child, td:first-child { 
      text-align: left; 
    }
    thead tr { 
      background: #f2f2f2; 
    }
    table { 
      border: 1px solid #000;   /* ✅ outer border */
    }
  </style>

  <div style="text-align:center; margin-bottom:20px;">
    <h2 style="margin:0;">${this.storeName}</h2>
    <h4 style="margin:0;">Ledger Report</h4>
    <div><b>Ledger:</b> ${this.ledgerName}</div>
    <div><b>Basis:</b> ${this.reportBasis}</div>
    <div><b>Report Date:</b> ${formatDate(this.minDate)} - ${formatDate(this.maxDate)}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Voucher No</th>
        <th>Voucher Date</th>
        <th>Narration</th>
        <th>Debit</th>
        <th>Credit</th>
        <th>Balance</th>
      </tr>
    </thead>
    <tbody>
`;


    this.ledgerDetails.forEach(entry => {
      printContents += `
        <tr>
          <td>${entry.VoucherNo}</td>
          <td>${formatDate(entry.VoucherDate)}</td>
          <td>${entry.Narration}</td>
          <td>${entry.Debit?.toFixed(2) || '0.00'}</td>
          <td>${entry.Credit?.toFixed(2) || '0.00'}</td>
          <td>${entry.Balance?.toFixed(2) || '0.00'}</td>
        </tr>
      `;
    });

    printContents += `</tbody></table>`;

    const printWindow = window.open('', '', 'height=600,width=800');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Ledger Report</title></head><body>');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
      setTimeout(() => printWindow.close(), 50);
    }
  }

}